/**
 * avwapConfluenceZones — detect AVWAP convergence zones.
 *
 * Per Brian Shannon (`Maximum Trading Gains with Anchored VWAP`), the
 * single highest-edge AVWAP signal isn't ANY one anchor — it's the
 * **confluence zone** where multiple AVWAPs (anchored at different
 * pivots: PDH, PDL, PDC, session high, session low) converge within a
 * tight price band. Those zones act as institutional reference clusters
 * and have higher reaction probability than any one anchor in isolation.
 *
 * Closes the long-deferred v7.4.3 audit item §3.2 (`vwapConfluenceZone`)
 * per `docs/research/Coach_Improvements_v7.4.3.md` and slots into the
 * Top-Down level lexicon per `docs/research/Coach_TopDown_Analysis.md`
 * §5.1 / §5.2.
 *
 * Algorithm (single-link clustering on sorted AVWAP values):
 *   1. Snapshot all AVWAP anchor → value pairs at the target time.
 *   2. Sort the (anchor, value) pairs by value ascending.
 *   3. Walk the sorted list and group consecutive values whose gap to
 *      the previous group's max is ≤ tolerance.
 *   4. Emit any group containing ≥ 2 anchors as a zone with center =
 *      mean of contributing values.
 *
 * Tolerance is expressed in **price points** (not ticks) so the caller
 * can pass either an explicit tolerance or `tolTicks × tickSize` from
 * the instrument spec. Default 4 ticks at the standard 0.25 MNQ tick
 * size = 1.0 point tolerance.
 *
 * Pure function. No side effects.
 */

export const DEFAULT_OPTS = Object.freeze({
  /** Convergence tolerance in PRICE POINTS. The caller is responsible for
   *  translating "N ticks" via the instrument's tick size. */
  tolerancePoints: 1.0,
  /** Minimum number of anchors a zone must contain to be emitted. Default
   *  2 — i.e. any two AVWAPs converging is interesting; one AVWAP alone
   *  isn't a "zone" (just a level). */
  minAnchors: 2,
});

/**
 * Detect AVWAP confluence zones from a snapshot of {anchor: price} pairs.
 *
 * @param {Object<string, number|null>} snapshot
 *   Map of anchor key (e.g., 'prior-day-high', 'session-low') to its
 *   current AVWAP value. Anchors with null / non-finite values are
 *   silently skipped — happens when an anchor couldn't be resolved (e.g.,
 *   prior-day-high requested but only one trading day of bars exists).
 * @param {Object} [opts]
 * @param {number} [opts.tolerancePoints]  Convergence tolerance in price
 *   points. Defaults to 1.0. To express "4 ticks" pass `4 * tickSize`.
 * @param {number} [opts.minAnchors]  Minimum contributing anchors to
 *   call a cluster a zone. Default 2.
 * @returns {Array<{centerPrice:number, widthPoints:number, contributingAnchors:string[]}>}
 *   Zones sorted by center price ascending. Empty array when no
 *   convergence is detected.
 */
export const detectAvwapConfluenceZones = (snapshot, opts = {}) => {
  if (!snapshot || typeof snapshot !== 'object') return [];

  const tolerance = Number(opts.tolerancePoints ?? DEFAULT_OPTS.tolerancePoints);
  const minAnchors = Math.max(1, Math.floor(Number(opts.minAnchors ?? DEFAULT_OPTS.minAnchors)));
  if (!Number.isFinite(tolerance) || tolerance <= 0) return [];

  // Project the snapshot into a sortable list, dropping any non-finite
  // values. Order of insertion into the original object is preserved on
  // ties so the contributingAnchors arrays read in a stable order.
  const pairs = [];
  for (const [anchor, value] of Object.entries(snapshot)) {
    const v = Number(value);
    if (Number.isFinite(v)) pairs.push({ anchor, value: v });
  }
  if (pairs.length < minAnchors) return [];

  pairs.sort((a, b) => a.value - b.value);

  // Single-link cluster walk: keep growing the current cluster as long as
  // the next pair is within `tolerance` of the current cluster's MAX
  // value. Comparing to max (rather than min or center) means each pair
  // only needs to be close to the most recent inclusion — keeps the
  // grouping resilient to a "string" of pairs that drift slowly across
  // the band. (Comparing to the cluster's MIN would split string-shaped
  // clusters that span > tolerance overall; comparing to the running
  // center would mean each new addition shifts the bar, again splitting.)
  const clusters = [];
  let current = [pairs[0]];
  let currentMax = pairs[0].value;

  for (let i = 1; i < pairs.length; i++) {
    const p = pairs[i];
    if (p.value - currentMax <= tolerance) {
      current.push(p);
      if (p.value > currentMax) currentMax = p.value;
    } else {
      clusters.push(current);
      current = [p];
      currentMax = p.value;
    }
  }
  clusters.push(current);

  // Emit zones for clusters meeting the minAnchors threshold.
  const zones = [];
  for (const cluster of clusters) {
    if (cluster.length < minAnchors) continue;
    const values = cluster.map(c => c.value);
    const centerPrice = values.reduce((s, v) => s + v, 0) / values.length;
    const widthPoints = Math.max(...values) - Math.min(...values);
    zones.push({
      centerPrice,
      widthPoints,
      contributingAnchors: cluster.map(c => c.anchor),
    });
  }

  return zones;
};

/**
 * Convenience: derive zones for a trade's barAnalysis snapshot.
 *
 * Takes the AVWAP-at-entry map already persisted on `trade.barAnalysis.
 * avwapAtEntry` (populated by maeMfe via avwap.js) and produces the
 * Top-Down §5.1 `avwapConfluenceZones` field. Falls back to defaults
 * for missing tickSize / tolerance.
 *
 * @param {Object} avwapAtEntry   { anchorKey: price | null }
 * @param {number} [tickSize]     Instrument tick size (e.g. 0.25 for MNQ).
 *                                If provided, tolerance = 4 × tickSize.
 * @param {Object} [opts]         Additional override opts.
 */
export const deriveAvwapConfluenceZonesForTrade = (avwapAtEntry, tickSize, opts = {}) => {
  let tolerancePoints = opts.tolerancePoints;
  if (!Number.isFinite(tolerancePoints) && Number.isFinite(tickSize) && tickSize > 0) {
    tolerancePoints = 4 * tickSize;
  }
  return detectAvwapConfluenceZones(avwapAtEntry, {
    ...opts,
    ...(Number.isFinite(tolerancePoints) ? { tolerancePoints } : {}),
  });
};
